/**
 * regional-calendar-data — the data adapter behind the Regional Calendar screen
 * (calendar insertion, region layer; decisions in docs/calendar-data-type-map.md,
 * blueprint docs/floor-calendar-port.md).
 *
 * The region board draws a branch roster with three live signals per branch and
 * derives its own health score from them (the prototype's factor model). What this
 * adapter pushes arrives through the serving path:
 *   branch roster (region-scoped) . getQueueDashboard, scope "region"
 *   wait + volume per branch ...... the same dashboard's per-location rows
 *   no-show rate per branch ....... getAppointmentsReport, one read per branch
 *   branch manager ................ listStaff, matched on the branch's own row
 *   branch time zone .............. listLocations (useLocations)
 *   region SLA .................... median slaTargetMins across served queues
 *
 * WHY NOT getRegionOverview: it is the natural source for the roster and it is
 * UNBOUND in the design environment — GET /v1/admin/workforce/region/{id}/overview
 * answers 501 ROUTE_UNBOUND (it is also why the existing Regional overview screen
 * shows an error). Rather than sit behind an unbound route, this adapter composes
 * the roster from reads that resolve today; it collapses to that single read when
 * the binding lands (REQ-CAL-6).
 *
 * SESSION-vs-SERVING region ids: sessionScope() returns the assignment id (e.g.
 * `region-ne`); the queue-reporting family keys regions as `rg_east` etc. When the
 * session id doesn't resolve, the dashboard's own server-resolved default region is
 * used and named honestly from `scopeName` — the mismatch is REQ-090's territory,
 * not something to paper over with a hardcoded map.
 *
 * Health score is DERIVED by the board from these signals, as ratified — it swaps
 * to one served read when REQ-CAL-1's getStoreHealth lands.
 *
 * No fixture data lives in this file — the serving-path rule and the
 * no-fake-interactivity mandate both apply in full.
 */
import { useEffect, useMemo, useState } from "react";
import { getQueueDashboard, listQueues } from "../queues/queues-api";
import { sessionScope, NO_REGION_SCOPE_MSG } from "../workforce/wf-api";
import { getAppointmentsReport } from "../appointments/appointments-api";
import { useLocations } from "../locations/useLocations";
import { listStaff } from "@v0y4ge/api-client";

export interface RegionStoreRow {
  id: string;
  name: string;
  manager: string;
  /** minutes — the branch's average wait over the range */
  wait: number;
  /** percent — share of today's appointments that were no-shows */
  ns: number;
  /** how busy this branch is against the region average; 1 = at the average */
  demand: number;
  tz?: string;
}

export interface RegionSnapshot {
  regionId: string;
  name: string;
  sla: number | null;
  tz: string | null;
  stores: RegionStoreRow[];
  sources: { op: string; ok: boolean; error?: string }[];
}

export interface RegionCalendarState {
  region: RegionSnapshot | null;
  loading: boolean;
  error: string | null;
  /** the session carries no region assignment — REQ-090; nothing can be shown */
  scopeMissing: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const num = (v: unknown, fallback = 0) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);

const median = (xs: number[]): number | null => {
  const s = xs.filter((x) => Number.isFinite(x) && x > 0).sort((a, b) => a - b);
  if (!s.length) return null;
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
};

type StaffRow = {
  name?: string; role?: string; title?: string;
  locations?: { locationId?: string; home?: boolean }[];
};

/** The branch's manager, from the served roster: a manager-titled member whose
 *  own location row points at that branch. No name is invented if none matches. */
const managerFor = (staff: StaffRow[], locationId: string): string => {
  const isManager = (s: StaffRow) => /manager/i.test(String(s.role ?? "")) || /manager/i.test(String(s.title ?? ""));
  const at = (s: StaffRow) => (s.locations ?? []).some((l) => l.locationId === locationId);
  const hit = staff.find((s) => isManager(s) && at(s) && (s.locations ?? []).some((l) => l.locationId === locationId && l.home))
    ?? staff.find((s) => isManager(s) && at(s));
  return String(hit?.name ?? "");
};

export function useRegionCalendar(): RegionCalendarState {
  const { regionId: sessionRegionId } = useMemo(() => sessionScope(), []);
  const { locations } = useLocations();
  /* Stable dep: the tz map only changes when the location set does. */
  const locKey = locations.map((l) => l.id).join(",");
  const [state, setState] = useState<RegionCalendarState>({
    region: null, loading: !!sessionRegionId, error: null, scopeMissing: !sessionRegionId,
  });

  useEffect(() => {
    if (!sessionRegionId) {
      setState({ region: null, loading: false, error: null, scopeMissing: true });
      return;
    }
    let alive = true;
    setState((s) => ({ ...s, loading: true, scopeMissing: false }));

    const sources: { op: string; ok: boolean; error?: string }[] = [];
    const grab = async <X,>(op: string, p: Promise<X>): Promise<X | null> => {
      try { const v = await p; sources.push({ op, ok: true }); return v; }
      catch (e) { sources.push({ op, ok: false, error: e instanceof Error ? e.message : String(e) }); return null; }
    };

    (async () => {
      const [dash, queues, staffPage] = await Promise.all([
        grab("getQueueDashboard", getQueueDashboard({ range: "today", scope: "region", regionId: sessionRegionId })),
        grab("listQueues", listQueues(null)),
        grab("listStaff", (listStaff as unknown as (q?: Record<string, unknown>) => Promise<unknown>)({ limit: 200 })),
      ]);
      if (!alive) return;

      if (!dash) {
        setState({
          region: null, loading: false, scopeMissing: false,
          error: sources.find((s) => s.op === "getQueueDashboard" && !s.ok)?.error
            ?? "The region's queue reporting could not be read.",
        });
        return;
      }

      /* Rows are per LOCATION at region scope; the dashboard also reports which
       * region it actually resolved, which is the honest region identity here. */
      const rows = (dash.rows ?? []).filter((r) => r.kind === "location");
      const resolvedId = dash.regionId ?? sessionRegionId;
      const inRegion = (dash.locations ?? []).filter((l) => !dash.regionId || l.regionId === dash.regionId);
      const roster = (inRegion.length ? inRegion : rows.map((r) => ({ id: r.id, name: r.name, regionId: resolvedId })));

      if (!roster.length) {
        setState({
          region: null, loading: false, scopeMissing: false,
          error: "The region resolved with no branches — nothing to draw.",
        });
        return;
      }

      const rowFor = (id: string) => rows.find((r) => r.id === id) ?? null;
      const volumes = roster.map((b) => num(rowFor(b.id)?.completed, 0));
      const counted = volumes.filter((v) => v > 0);
      const meanVol = counted.length ? counted.reduce((a, v) => a + v, 0) / counted.length : 0;

      /* No-show rate is only served per location, so it is one read per branch —
       * scope-faithful by construction (each branch's own dataset). */
      const nsRates = await Promise.all(roster.map(async (b) => {
        const rep = await grab(`getAppointmentsReport:${b.id}`, getAppointmentsReport({ range: "today", locationId: b.id }));
        const rate = num(rep?.kpis?.noShowRate, NaN);
        return Number.isFinite(rate) ? Math.round(rate > 1 ? rate : rate * 100) : 0;
      }));
      if (!alive) return;

      const staff = (((staffPage as { data?: unknown[]; staff?: unknown[] } | null)?.data
        ?? (staffPage as { staff?: unknown[] } | null)?.staff ?? []) as StaffRow[]);

      const tzOf = new Map<string, string>();
      locations.forEach((l) => {
        const raw = (l.raw ?? {}) as Record<string, unknown>;
        const tz = String(raw.timezone ?? "");
        if (tz) tzOf.set(l.id, tz);
      });

      const stores: RegionStoreRow[] = roster.map((b, i) => {
        const row = rowFor(b.id);
        const vol = num(row?.completed, 0);
        return {
          id: b.id,
          name: b.name,
          manager: managerFor(staff, b.id),
          wait: Math.round(num(row?.avgWaitMins, 0)),
          ns: nsRates[i],
          demand: meanVol > 0 && vol > 0 ? clamp(Number((vol / meanVol).toFixed(2)), 0.5, 1.5) : 1,
          tz: tzOf.get(b.id),
        };
      });

      /* Region time zone: the zone most of its branches keep. A single
       * out-of-zone branch then reads as the exception it is, which is exactly
       * what the board's mixed-clock note is for. */
      const tzCount = new Map<string, number>();
      stores.forEach((s) => { if (s.tz) tzCount.set(s.tz, (tzCount.get(s.tz) ?? 0) + 1); });
      const tz = [...tzCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

      const branchIds = new Set(stores.map((s) => s.id));
      /* listQueues returns QueueListPage — `{ data, pagination }` per the
       * contract's QueueListResponse. Reading `.queues` here was both a
       * typecheck error (TS2339) and, at runtime, an empty median behind the
       * `?? []`, so the region SLA silently read 0. */
      const sla = median((queues?.data ?? [])
        .filter((q) => !q.locationId || branchIds.has(q.locationId))
        .map((q) => num(q.slaTargetMins, 0)));

      setState({
        region: {
          regionId: resolvedId,
          name: dash.scopeName || "",
          sla, tz, stores, sources,
        },
        loading: false, error: null, scopeMissing: false,
      });
    })();

    return () => { alive = false; };
  }, [sessionRegionId, locKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

/** The board's REGION shape. Fields the region does not serve are omitted, so the
 *  board keeps its own value rather than being handed a derived guess. */
export function toRegionData(r: RegionSnapshot): Record<string, unknown> {
  const region: Record<string, unknown> = {};
  if (r.name) region.name = r.name;
  if (r.sla != null) region.sla = r.sla;
  if (r.tz) region.tz = r.tz;
  return {
    region,
    stores: r.stores.map((s) => ({
      id: s.id, name: s.name, manager: s.manager,
      wait: s.wait, ns: s.ns, demand: s.demand,
      ...(s.tz ? { tz: s.tz } : {}),
    })),
  };
}

export { NO_REGION_SCOPE_MSG };
